import React from "react";
import { getExpectedStartTimeOptions, normalizeExpectedStartTime } from "./expectedStartTime";
import ReactDOM from "react-dom/client";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    TextField,
    Box,
    Fade,
    Grid,
    MenuItem,
    Autocomplete,
    FormControlLabel,
    CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { StyledEngineProvider } from "@mui/material/styles";
import ThemeProvider from "../../theme";
import { LocalizationProvider } from "../../i18n/LocalizationProvider";
import Iconify from "../iconify";
import { enqueueSnackbar } from "notistack";
import axios from "axios";
import { useTranslation } from "react-i18next";

const sanitizeMobileNumber = (value) =>
    String(value ?? "").replace(/[^0-9\u0660-\u0669\u06F0-\u06F9]/g, "");

const formatFrappeDateTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const deriveInitialOrderMode = (initialData = {}, isCallCenterUser = false) => {
    if (initialData.delivery_provider || initialData.use_delivery_provider) {
        return "delivery";
    }
    if (
        initialData.designated_number !== null &&
        initialData.designated_number !== undefined &&
        String(initialData.designated_number).trim() !== ""
    ) {
        return "dine_in";
    }
    if (initialData.is_existing_draft) {
        return "takeaway";
    }
    return isCallCenterUser ? "delivery" : "dine_in";
};

const ReservationPromptDialog = ({ onResolve, onReject, options = {} }) => {
    const { t } = useTranslation();
    const isTabsPos = !!options?.isTabsPos;
    const isCallCenterUser = !!options?.isCallCenterUser;
    const initialData = options?.initialData || {};
    const showDraftAlertField = !!options?.enableDraftAlert;
    const showExpectedStartTime = !!options?.enableExpectedStartTime;
    const [currentTime, setCurrentTime] = React.useState(() => new Date());
    React.useEffect(() => {
        if (!showExpectedStartTime) return undefined;
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, [showExpectedStartTime]);
    const expectedStartTimeOptions = React.useMemo(
        () => getExpectedStartTimeOptions(currentTime),
        [currentTime]
    );
    const initialOrderMode = React.useMemo(
        () => deriveInitialOrderMode(initialData, isCallCenterUser),
        [initialData, isCallCenterUser]
    );

    const estimatedPreparationTimeOptions = React.useMemo(() => {
        const list = [];
        for (let t = 15; t <= 90; t += 5) list.push(t);
        return list;
    }, []);

    const alertAfterOptions = React.useMemo(
        () => ["", 15, 30, 45, 60, 90, 120, 180],
        []
    );

    const reservedDesignatedNumbers = React.useMemo(() => {
        const raw = Array.isArray(options?.reservedDesignatedNumbers)
            ? options.reservedDesignatedNumbers
            : [];
        const nums = raw
            .map((v) => parseInt(v, 10))
            .filter((n) => Number.isFinite(n) && n >= 1 && n <= 200);
        return new Set(nums);
    }, [options?.reservedDesignatedNumbers]);

    // The order's own designated number should always be selectable even if
    // it appears in the reserved set (it's reserved by *this* order).
    const ownDesignatedNumber = React.useMemo(() => {
        const raw = initialData.designated_number;
        if (raw === null || raw === undefined || raw === "") return null;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    }, [initialData.designated_number]);

    const designatedNumberOptions = React.useMemo(() => {
        const list = [];
        for (let n = 1; n <= 200; n += 1) {
            if (!reservedDesignatedNumbers.has(n) || n === ownDesignatedNumber) {
                list.push(n);
            }
        }
        return list;
    }, [reservedDesignatedNumbers, ownDesignatedNumber]);

    const designatedNumberTypeaheadOptions = React.useMemo(
        () => {
            const options = [
                {
                    value: "DINE_IN",
                    label: t("pos.reservationPrompt.orderModeDineIn", {
                        defaultValue: "Dine in",
                    }),
                },
                ...designatedNumberOptions.map((n) => ({
                    value: String(n),
                    label: String(n),
                })),
            ];

            return options;
        },
        [designatedNumberOptions, t]
    );

    const [formData, setFormData] = React.useState(() => {
        if (isTabsPos) {
            // designated_number must be a string to match <MenuItem value={String(n)}>
            const rawDN = initialData.designated_number;
            const designatedStr =
                rawDN === null || rawDN === undefined || rawDN === ""
                    ? ""
                    : String(rawDN);
            return {
                notes: initialData.notes || "",
                ...(showExpectedStartTime ? { expected_start_time: normalizeExpectedStartTime(initialData.expected_start_time) } : {}),
                estimated_preparation_time: initialData.estimated_preparation_time || 15,
                alert_after_minutes:
                    initialData.alert_after_minutes === null ||
                    initialData.alert_after_minutes === undefined ||
                    initialData.alert_after_minutes === ""
                        ? ""
                        : String(initialData.alert_after_minutes),
                designated_number: designatedStr,
                order_mode: initialOrderMode,
                use_delivery_provider: initialOrderMode === "delivery",
                delivery_provider: initialData.delivery_provider || "",
                customer: initialData.customer || "",
                mobile_no: sanitizeMobileNumber(initialData.mobile_no),
                address_line1: initialData.address_line1 || "",
                address_line2: initialData.address_line2 || "",
                customer_details: initialData.customer_details || "",
            };
        }
        return {
            notes: initialData.notes || "",
            ...(showExpectedStartTime ? { expected_start_time: normalizeExpectedStartTime(initialData.expected_start_time) } : {}),
            adults: initialData.adults || "",
            kids: initialData.kids || "",
            babies: initialData.babies || "",
        };
    });
    const [open, setOpen] = React.useState(true);

    const {
        title = "Reservation Details",
        message = "Please enter the reservation details:",
        confirmText = "Confirm",
        cancelText = "Cancel",
        showSaveDraftButton = false,
        saveDraftText = "Save",
    } = options;

    const dialogTitle = React.useMemo(() => {
        if (!isTabsPos) return title;
        const modeLabelMap = {
            dine_in: t("pos.reservationPrompt.orderModeDineIn", {
                defaultValue: "Dine in",
            }),
            takeaway: t("pos.reservationPrompt.orderModeTakeaway", {
                defaultValue: "Takeaway",
            }),
            delivery: t("pos.reservationPrompt.orderModeDelivery", {
                defaultValue: "Delivery",
            }),
        };
        return `${title} (${modeLabelMap[formData?.order_mode] || modeLabelMap.takeaway})`;
    }, [isTabsPos, title, formData?.order_mode, t]);

    const handleInputChange = (field, value) => {
        latestFormDataRef.current = {
            ...(latestFormDataRef.current || {}),
            [field]: value,
        };
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleNumberChange = (field, value) => {
        // Allow empty string, otherwise ensure it's a valid positive number
        if (value === "" || value === null || value === undefined) {
            handleInputChange(field, "");
        } else {
            const numValue = Math.max(0, parseInt(value) || 0);
            handleInputChange(field, numValue);
        }
    };

    const isDeliveryMode = formData.order_mode === "delivery";
    const showDesignatedNumberField =
        !isCallCenterUser && !isDeliveryMode && formData.order_mode === "dine_in";

    const handleOrderModeChange = React.useCallback((nextMode) => {
        setFormData((prev) => {
            const next = {
                ...prev,
                order_mode: nextMode,
                use_delivery_provider: nextMode === "delivery",
                designated_number:
                    nextMode === "dine_in"
                        ? prev.designated_number || "DINE_IN"
                        : "",
            };
            latestFormDataRef.current = next;
            return next;
        });
    }, []);

    const [deliveryProviders, setDeliveryProviders] = React.useState([]);
    const [deliveryProvidersLoading, setDeliveryProvidersLoading] = React.useState(false);
    const [deliveryProviderInputValue, setDeliveryProviderInputValue] = React.useState(
        initialData.delivery_provider || ""
    );

    const fetchDeliveryProviders = React.useCallback(async () => {
        setDeliveryProvidersLoading(true);
        try {
            const params = {
                fields: JSON.stringify(["name", "company", "disabled"]),
                limit_page_length: 0,
            };
            const { data } = await axios.get("resource/Delivery Providers", { params });
            const list = (Array.isArray(data?.data) ? data.data : []).filter(
                (provider) => Number(provider?.disabled) !== 1
            );
            setDeliveryProviders(list);
            return list;
        } finally {
            setDeliveryProvidersLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isTabsPos) return;
        fetchDeliveryProviders().then((list) => {
            // If we have a delivery provider from initialData, just ensure the input shows it
            if (formData.delivery_provider && Array.isArray(list)) {
                const match = list.find((p) => p?.name === formData.delivery_provider);
                const currentInput = String(deliveryProviderInputValue || "").trim();
                const shouldHydrateLabel =
                    !currentInput || currentInput === String(formData.delivery_provider);
                if (match && shouldHydrateLabel) {
                    // setDeliveryProviderInputValue(match.company || match.name);
                }
            } else if (!formData.delivery_provider && Array.isArray(list) && list.length > 0) {
                setFormData((prev) => {
                    const next = {
                        ...prev,
                        delivery_provider: list[0].name,
                    };
                    latestFormDataRef.current = next;
                    return next;
                });
                // setDeliveryProviderInputValue(list[0].company ||
                //  list[0].name);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTabsPos, formData.use_delivery_provider]);

    const [customerOptions, setCustomerOptions] = React.useState([]);
    const [customerLoading, setCustomerLoading] = React.useState(false);
    const [customerFromMobileLoading, setCustomerFromMobileLoading] =
        React.useState(false);
    const [customerInputValue, setCustomerInputValue] = React.useState(
        initialData.customer || ""
    );
    const [mobileOptions, setMobileOptions] = React.useState([]);
    const [mobileLoading, setMobileLoading] = React.useState(false);
    const [mobileInputValue, setMobileInputValue] = React.useState(
        sanitizeMobileNumber(initialData.mobile_no)
    );
    const isSyncingRef = React.useRef(false);
    const customerSearchRequestIdRef = React.useRef(0);
    const mobileSearchRequestIdRef = React.useRef(0);
    const hydratedCustomerRef = React.useRef(null);
    const hydratedMobileRef = React.useRef(null);
    const userEditedDeliveryFieldsRef = React.useRef({
        mobile_no: false,
        address_line1: false,
        address_line2: false,
        customer_details: false,
    });
    const initialDeliveryCustomerRef = React.useRef(
        String(initialData.customer || "").trim()
    );
    const latestFormDataRef = React.useRef(formData);

    React.useEffect(() => {
        latestFormDataRef.current = formData;
    }, [formData]);

    const resetDeliveryDirtyFields = React.useCallback(() => {
        userEditedDeliveryFieldsRef.current = {
            mobile_no: false,
            address_line1: false,
            address_line2: false,
            customer_details: false,
        };
    }, []);

    const markDeliveryFieldDirty = React.useCallback((field) => {
        if (Object.prototype.hasOwnProperty.call(userEditedDeliveryFieldsRef.current, field)) {
            userEditedDeliveryFieldsRef.current[field] = true;
        }
    }, []);

    const normalizeHtmlText = React.useCallback((html) => {
        if (!html) return "";
        try {
            return String(html).replace(/<[^>]+>/g, "").trim();
        } catch {
            return String(html);
        }
    }, []);

    const parsePrimaryAddressLines = React.useCallback((primaryAddress) => {
        const raw = String(primaryAddress || "");
        if (!raw) return { line1: "", line2: "" };
        const parts = raw.split("<br>").map((p) => normalizeHtmlText(p)).filter(Boolean);
        return { line1: parts[0] || "", line2: parts[1] || "" };
    }, [normalizeHtmlText]);

    const fetchAddressByName = React.useCallback(async (addressName) => {
        const name = String(addressName || "").trim();
        if (!name) return { line1: "", line2: "" };
        try {
            const { data } = await axios.get(
                "resource/Address/" + encodeURIComponent(name)
            );
            const doc = data?.data || {};
            return {
                line1: doc.address_line1 || "",
                line2: doc.address_line2 || "",
            };
        } catch {
            return { line1: "", line2: "" };
        }
    }, []);

    const fetchLatestAddressByCustomer = React.useCallback(async (customerName) => {
        const customer = String(customerName || "").trim();
        if (!customer) return { line1: "", line2: "" };
        try {
            const params = {
                fields: JSON.stringify(["parent"]),
                filters: JSON.stringify([
                    ["Dynamic Link", "link_doctype", "=", "Customer"],
                    ["Dynamic Link", "link_name", "=", customer],
                    ["Dynamic Link", "parenttype", "=", "Address"],
                ]),
                order_by: "modified desc",
                limit_page_length: 1,
            };
            const { data } = await axios.get("resource/Dynamic Link", { params });
            const linkRow = Array.isArray(data?.data) ? data.data[0] : null;
            if (linkRow?.parent) {
                return await fetchAddressByName(linkRow.parent);
            }
        } catch {
            // Fallback below
        }

        // Fallback for setups where Dynamic Link is restricted.
        try {
            const params = {
                fields: JSON.stringify(["name", "address_line1", "address_line2"]),
                filters: JSON.stringify([["Address", "address_title", "=", customer]]),
                order_by: "modified desc",
                limit_page_length: 1,
            };
            const { data } = await axios.get("resource/Address", { params });
            const row = Array.isArray(data?.data) ? data.data[0] : null;
            return {
                line1: row?.address_line1 || "",
                line2: row?.address_line2 || "",
            };
        } catch {
            return { line1: "", line2: "" };
        }
    }, [fetchAddressByName]);

    const fetchCustomerNotesByName = React.useCallback(async (customerName) => {
        const name = String(customerName || "").trim();
        if (!name) return "";
        try {
            const params = {
                fields: JSON.stringify(["customer_details"]),
                filters: JSON.stringify([["Customer", "name", "=", name]]),
                limit_page_length: 1,
            };
            const { data } = await axios.get("resource/Customer", { params });
            const row = Array.isArray(data?.data) ? data.data[0] : null;
            return row?.customer_details || "";
        } catch {
            return "";
        }
    }, []);

    const resolveCustomerDetails = React.useCallback(
        async (customerDoc = {}) => {
            const nextMobile = sanitizeMobileNumber(customerDoc?.mobile_no);
            let nextLine1 = "";
            let nextLine2 = "";

            // Notes live on Customer.customer_details. Some lookups (e.g. the
            // mobile dropdown) don't carry it, so fall back to a by-name fetch.
            let nextDetails =
                typeof customerDoc?.customer_details === "string"
                    ? customerDoc.customer_details
                    : null;
            if (nextDetails === null) {
                nextDetails = await fetchCustomerNotesByName(
                    customerDoc?.name || customerDoc?.customer_name
                );
            }

            const primaryAddress = customerDoc?.primary_address || "";
            if (!primaryAddress) {
                const linkedAddress = await fetchLatestAddressByCustomer(
                    customerDoc?.name || customerDoc?.customer_name
                );
                return {
                    mobile_no: nextMobile,
                    address_line1: linkedAddress.line1 || "",
                    address_line2: linkedAddress.line2 || "",
                    customer_details: nextDetails || "",
                };
            }

            const parsedInline = parsePrimaryAddressLines(primaryAddress);
            if (parsedInline.line1 || parsedInline.line2) {
                nextLine1 = parsedInline.line1 || "";
                nextLine2 = parsedInline.line2 || "";
            } else {
                const resolved = await fetchAddressByName(primaryAddress);
                nextLine1 = resolved.line1 || "";
                nextLine2 = resolved.line2 || "";
            }

            return {
                mobile_no: nextMobile,
                address_line1: nextLine1,
                address_line2: nextLine2,
                customer_details: nextDetails || "",
            };
        },
        [
            fetchAddressByName,
            fetchLatestAddressByCustomer,
            parsePrimaryAddressLines,
            fetchCustomerNotesByName,
        ]
    );

    const fetchCustomerMobileNumbersFallback = React.useCallback(
        async ({ customer, mobile } = {}) => {
            const customerValue = String(customer || "").trim();
            const mobileValue = String(mobile || "").trim();
            if (!customerValue && !mobileValue) return [];

            try {
                const filters = customerValue
                    ? [["Customer", "name", "=", customerValue]]
                    : [["Customer", "mobile_no", "like", `%${mobileValue}%`]];
                const params = {
                    fields: JSON.stringify([
                        "name",
                        "customer_name",
                        "mobile_no",
                        "primary_address",
                        "customer_details",
                    ]),
                    filters: JSON.stringify(filters),
                    order_by: "modified desc",
                    limit_page_length: 20,
                };
                const { data } = await axios.get("resource/Customer", { params });
                const list = Array.isArray(data?.data) ? data.data : [];
                return list.filter((row) => sanitizeMobileNumber(row?.mobile_no));
            } catch {
                return [];
            }
        },
        []
    );

    const fetchCustomerMobileNumbers = React.useCallback(
        async ({ customer, mobile } = {}) => {
            const customerValue = String(customer || "").trim();
            const mobileValue = String(mobile || "").trim();
            if (!customerValue && !mobileValue) return [];

            try {
                const { data } = await axios.get(
                    "method/restaurant_management.api.get_customer_mobile_numbers",
                    {
                        params: {
                            customer: customerValue || undefined,
                            mobile: mobileValue || undefined,
                            limit: 20,
                        },
                    }
                );
                const list = Array.isArray(data?.message) ? data.message : [];
                if (list.length) return list;
                return await fetchCustomerMobileNumbersFallback({
                    customer: customerValue,
                    mobile: mobileValue,
                });
            } catch {
                return await fetchCustomerMobileNumbersFallback({
                    customer: customerValue,
                    mobile: mobileValue,
                });
            }
        },
        [fetchCustomerMobileNumbersFallback]
    );

    const applySelectedCustomer = React.useCallback(
        async (
            customerDoc = {},
            { force = true, preserveCustomerInput = false } = {}
        ) => {
            const customerName = String(customerDoc?.name || "").trim();
            if (!customerName) return null;

            const customerLabel = String(
                customerDoc?.customer_name || customerName
            ).trim();
            const hydrated = await resolveCustomerDetails(customerDoc);

            isSyncingRef.current = true;
            try {
                const currentData = latestFormDataRef.current || {};
                const currentCustomer = String(currentData.customer || "").trim();
                const shouldSetCustomer =
                    !preserveCustomerInput || !currentCustomer;
                const shouldSetMobile =
                    force ||
                    (!userEditedDeliveryFieldsRef.current.mobile_no &&
                        !String(currentData.mobile_no || "").trim());
                const shouldSetLine1 =
                    force ||
                    (!userEditedDeliveryFieldsRef.current.address_line1 &&
                        !String(currentData.address_line1 || "").trim());
                const shouldSetLine2 =
                    force ||
                    (!userEditedDeliveryFieldsRef.current.address_line2 &&
                        !String(currentData.address_line2 || "").trim());
                const shouldSetDetails =
                    force ||
                    (!userEditedDeliveryFieldsRef.current.customer_details &&
                        !String(currentData.customer_details || "").trim());
                const nextMobile = shouldSetMobile
                    ? hydrated.mobile_no || ""
                    : currentData.mobile_no;

                if (shouldSetMobile) {
                    setMobileInputValue(nextMobile);
                }
                if (shouldSetCustomer) {
                    setCustomerInputValue(customerLabel || customerName);
                }

                setFormData((prev) => {
                    const next = {
                        ...prev,
                        customer: shouldSetCustomer
                            ? customerLabel || customerName
                            : prev.customer,
                        mobile_no: shouldSetMobile ? nextMobile : prev.mobile_no,
                        address_line1: shouldSetLine1
                            ? hydrated.address_line1 || ""
                            : prev.address_line1,
                        address_line2: shouldSetLine2
                            ? hydrated.address_line2 || ""
                            : prev.address_line2,
                        customer_details: shouldSetDetails
                            ? hydrated.customer_details || ""
                            : prev.customer_details,
                    };
                    latestFormDataRef.current = next;
                    return next;
                });
                hydratedCustomerRef.current = customerName;
                if (hydrated.mobile_no) {
                    hydratedMobileRef.current = sanitizeMobileNumber(hydrated.mobile_no);
                }
            } finally {
                isSyncingRef.current = false;
            }

            const customerMobileOptions = await fetchCustomerMobileNumbers({
                customer: customerName,
            });
            if (customerMobileOptions.length) {
                setMobileOptions(customerMobileOptions);
            }

            return {
                customer: customerName,
                customer_name: customerLabel,
                ...hydrated,
            };
        },
        [fetchCustomerMobileNumbers, resolveCustomerDetails]
    );

    const searchCustomers = React.useCallback(async (q) => {
        const query = String(q || "").trim();
        if (!query) {
            setCustomerOptions([]);
            return;
        }
        const requestId = customerSearchRequestIdRef.current + 1;
        customerSearchRequestIdRef.current = requestId;
        setCustomerLoading(true);
        try {
            const params = {
                fields: JSON.stringify([
                    "name",
                    "customer_name",
                    "mobile_no",
                    "primary_address",
                    "customer_details",
                    "modified",
                ]),
                filters: JSON.stringify([["Customer", "customer_name", "like", `%${query}%`]]),
                order_by: "modified desc",
                limit_page_length: 20,
            };
            const { data } = await axios.get("resource/Customer", { params });
            const list = Array.isArray(data?.data) ? data.data : [];
            if (requestId === customerSearchRequestIdRef.current) {
                setCustomerOptions(list);
            }
        } finally {
            if (requestId === customerSearchRequestIdRef.current) {
                setCustomerLoading(false);
            }
        }
    }, []);

    const searchCustomersByMobile = React.useCallback(async (q) => {
        const query = String(q || "").trim();
        const requestId = mobileSearchRequestIdRef.current + 1;
        mobileSearchRequestIdRef.current = requestId;

        if (!query) {
            const selectedCustomer = String(
                latestFormDataRef.current?.customer || ""
            ).trim();
            if (!selectedCustomer) {
                setMobileOptions([]);
                return;
            }
            setMobileLoading(true);
            try {
                const list = await fetchCustomerMobileNumbers({
                    customer: selectedCustomer,
                });
                if (requestId === mobileSearchRequestIdRef.current) {
                    setMobileOptions(list);
                }
            } finally {
                if (requestId === mobileSearchRequestIdRef.current) {
                    setMobileLoading(false);
                }
            }
            return;
        }

        setMobileLoading(true);
        try {
            const list = await fetchCustomerMobileNumbers({ mobile: query });
            if (requestId === mobileSearchRequestIdRef.current) {
                setMobileOptions(list);
            }
        } finally {
            if (requestId === mobileSearchRequestIdRef.current) {
                setMobileLoading(false);
            }
        }
    }, [fetchCustomerMobileNumbers]);

    const resolveCustomerByValue = React.useCallback(
        async (rawCustomerValue) => {
            const customerValue = String(rawCustomerValue || "").trim();
            if (!customerValue) return null;

            const matched = customerOptions.find(
                (opt) =>
                    opt?.name === customerValue ||
                    String(opt?.customer_name || "").trim() === customerValue
            );
            if (matched) return matched;

            try {
                const paramsByName = {
                    fields: JSON.stringify([
                        "name",
                        "customer_name",
                        "mobile_no",
                        "primary_address",
                        "customer_details",
                    ]),
                    filters: JSON.stringify([["Customer", "name", "=", customerValue]]),
                    limit_page_length: 1,
                };
                const byNameRes = await axios.get("resource/Customer", {
                    params: paramsByName,
                });
                const byName = Array.isArray(byNameRes?.data?.data)
                    ? byNameRes.data.data[0]
                    : null;
                if (byName) return byName;
            } catch {
                // Fallback below.
            }

            try {
                const paramsByCustomerName = {
                    fields: JSON.stringify([
                        "name",
                        "customer_name",
                        "mobile_no",
                        "primary_address",
                        "customer_details",
                    ]),
                    filters: JSON.stringify([
                        ["Customer", "customer_name", "=", customerValue],
                    ]),
                    order_by: "modified desc",
                    limit_page_length: 1,
                };
                const byCustomerNameRes = await axios.get("resource/Customer", {
                    params: paramsByCustomerName,
                });
                return Array.isArray(byCustomerNameRes?.data?.data)
                    ? byCustomerNameRes.data.data[0] || null
                    : null;
            } catch {
                return null;
            }
        },
        [customerOptions]
    );

    const resolveCustomerByMobile = React.useCallback(
        async (rawMobileValue) => {
            const mobileValue = sanitizeMobileNumber(rawMobileValue);
            if (!mobileValue) return null;

            const exactOption = mobileOptions.find(
                (opt) => sanitizeMobileNumber(opt?.mobile_no) === mobileValue
            );
            if (exactOption) return exactOption;

            try {
                const params = {
                    fields: JSON.stringify([
                        "name",
                        "customer_name",
                        "mobile_no",
                        "primary_address",
                        "customer_details",
                    ]),
                    filters: JSON.stringify([
                        ["Customer", "mobile_no", "like", `%${mobileValue}%`],
                    ]),
                    order_by: "modified desc",
                    limit_page_length: 1,
                };
                const { data } = await axios.get("resource/Customer", { params });
                return Array.isArray(data?.data) ? data.data[0] || null : null;
            } catch {
                return null;
            }
        },
        [mobileOptions]
    );

    const canAutoFillCustomerFromMobile = React.useCallback(() => {
        const currentData = latestFormDataRef.current || {};
        return !String(currentData.customer || "").trim();
    }, []);

    const applyCustomerFromMobile = React.useCallback(
        async (customerDoc) => {
            if (!customerDoc) return null;
            const shouldShowCustomerLoading = canAutoFillCustomerFromMobile();
            if (shouldShowCustomerLoading) {
                setCustomerFromMobileLoading(true);
            }
            try {
                return await applySelectedCustomer(customerDoc, {
                    preserveCustomerInput: true,
                });
            } finally {
                if (shouldShowCustomerLoading) {
                    setCustomerFromMobileLoading(false);
                }
            }
        },
        [applySelectedCustomer, canAutoFillCustomerFromMobile]
    );

    const resolveAndApplyCustomerFromMobile = React.useCallback(
        async (mobileValue) => {
            const shouldShowCustomerLoading = canAutoFillCustomerFromMobile();
            if (shouldShowCustomerLoading) {
                setCustomerFromMobileLoading(true);
            }
            try {
                const resolved = await resolveCustomerByMobile(mobileValue);
                if (!resolved) return null;
                return await applySelectedCustomer(resolved, {
                    preserveCustomerInput: true,
                });
            } finally {
                if (shouldShowCustomerLoading) {
                    setCustomerFromMobileLoading(false);
                }
            }
        },
        [
            applySelectedCustomer,
            canAutoFillCustomerFromMobile,
            resolveCustomerByMobile,
        ]
    );

    React.useEffect(() => {
        if (!isTabsPos) return;
        const t = setTimeout(() => searchCustomers(customerInputValue), 300);
        return () => clearTimeout(t);
    }, [isTabsPos, customerInputValue, searchCustomers]);

    React.useEffect(() => {
        if (!isTabsPos) return;
        const t = setTimeout(() => searchCustomersByMobile(mobileInputValue), 300);
        return () => clearTimeout(t);
    }, [
        isTabsPos,
        mobileInputValue,
        searchCustomersByMobile,
    ]);

    // Load customer options on mount if we have a pre-filled customer
    React.useEffect(() => {
        if (!isTabsPos) return;
        if (initialData.customer && customerInputValue) {
            searchCustomers(customerInputValue);
        }
        if (initialData.mobile_no && mobileInputValue) {
            searchCustomersByMobile(mobileInputValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Hydrate delivery fields when reopening/editing an order that only has customer set.
    React.useEffect(() => {
        if (!isTabsPos) return;
        const customer = String(formData.customer || "").trim();
        if (!customer) return;
        if (customer !== initialDeliveryCustomerRef.current) return;
        if (hydratedCustomerRef.current === customer) return;
        const currentFormData = latestFormDataRef.current || {};
        if (currentFormData.mobile_no && currentFormData.address_line1) {
            hydratedCustomerRef.current = customer;
            return;
        }
        let isMounted = true;
        const hydrateFromCustomer = async () => {
            const customerDoc = await resolveCustomerByValue(customer);
            if (!isMounted || !customerDoc) return;
            await applySelectedCustomer(customerDoc, { force: false });
        };
        hydrateFromCustomer();
        return () => {
            isMounted = false;
        };
    }, [
        isTabsPos,
        formData.use_delivery_provider,
        formData.customer,
        resolveCustomerByValue,
        applySelectedCustomer,
    ]);

    const resolveWithAction = async (actionType = "confirm") => {
        const latestFormData = latestFormDataRef.current || formData;
        // Retain a saved historical time when editing other order details, but
        // reject a newly selected time that elapsed while the dialog was open.
        if (showExpectedStartTime && latestFormData.expected_start_time &&
            latestFormData.expected_start_time !== normalizeExpectedStartTime(initialData.expected_start_time) &&
            !getExpectedStartTimeOptions().includes(latestFormData.expected_start_time)) {
            enqueueSnackbar(t("pos.reservationPrompt.expectedStartTimeExpired"), { variant: "error" });
            setCurrentTime(new Date());
            return;
        }

        if (!isTabsPos) {
            if (latestFormData.adults === "" || latestFormData.adults <= 0) {
                enqueueSnackbar("Please enter the number of adults", {
                    variant: "error",
                });
                return;
            }
            const finalData = {
                ...latestFormData,
                adults: latestFormData.adults === "" ? 1 : latestFormData.adults,
                kids: latestFormData.kids === "" ? 0 : latestFormData.kids,
                babies: latestFormData.babies === "" ? 0 : latestFormData.babies,
                __action: actionType,
            };
            setOpen(false);
            onResolve(finalData);
            return;
        }

        if (isTabsPos) {
            let resolvedFormData = { ...latestFormData };
            if (resolvedFormData.use_delivery_provider) {
                resolvedFormData.customer = customerInputValue;
            }

            if (
                resolvedFormData.use_delivery_provider &&
                String(resolvedFormData.customer || "").trim() &&
                (!String(resolvedFormData.mobile_no || "").trim() ||
                    !String(resolvedFormData.address_line1 || "").trim())
            ) {
                const customerValue = String(resolvedFormData.customer || "").trim();
                const selectedCustomerDoc = await resolveCustomerByValue(customerValue);

                if (selectedCustomerDoc) {
                    const hydrated = await resolveCustomerDetails(selectedCustomerDoc);
                    resolvedFormData = {
                        ...resolvedFormData,
                        customer: selectedCustomerDoc.name || resolvedFormData.customer || "",
                        mobile_no: sanitizeMobileNumber(
                            resolvedFormData.mobile_no || hydrated?.mobile_no
                        ),
                        address_line1:
                            resolvedFormData.address_line1 ||
                            hydrated?.address_line1 ||
                            "",
                        address_line2:
                            resolvedFormData.address_line2 ||
                            hydrated?.address_line2 ||
                            "",
                    };
                }
            }

            if (
                resolvedFormData.use_delivery_provider &&
                !String(resolvedFormData.customer || "").trim() &&
                String(resolvedFormData.mobile_no || "").trim()
            ) {
                const selectedByMobile = await resolveCustomerByMobile(
                    resolvedFormData.mobile_no
                );
                if (selectedByMobile) {
                    const hydrated = await resolveCustomerDetails(selectedByMobile);
                    resolvedFormData = {
                        ...resolvedFormData,
                        customer: selectedByMobile.name || "",
                        mobile_no: sanitizeMobileNumber(
                            resolvedFormData.mobile_no || hydrated?.mobile_no
                        ),
                        address_line1:
                            resolvedFormData.address_line1 ||
                            hydrated?.address_line1 ||
                            "",
                        address_line2:
                            resolvedFormData.address_line2 ||
                            hydrated?.address_line2 ||
                            "",
                    };
                }
            }

            if (resolvedFormData.use_delivery_provider) {
                if (!resolvedFormData.delivery_provider) {
                    enqueueSnackbar(
                        t("pos.reservationPrompt.validationSelectDeliveryProvider", {
                            defaultValue: "Please select a delivery provider",
                        }),
                        {
                            variant: "error",
                        }
                    );
                    return;
                }
                if (!String(resolvedFormData.customer || "").trim()) {
                    enqueueSnackbar(
                        t("pos.reservationPrompt.validationEnterCustomer", {
                            defaultValue: "Please enter the customer",
                        }),
                        {
                            variant: "error",
                        }
                    );
                    return;
                }
                if (!String(resolvedFormData.mobile_no || "").trim()) {
                    enqueueSnackbar(
                        t("pos.reservationPrompt.validationEnterMobile", {
                            defaultValue: "Please enter the mobile number",
                        }),
                        {
                            variant: "error",
                        }
                    );
                    return;
                }
            }

            const prepTime = parseInt(
                resolvedFormData.estimated_preparation_time,
                10
            );
            const normalizedPrepTime = Number.isFinite(prepTime)
                ? prepTime
                : 15;

            const normalizedUseDeliveryProvider =
                resolvedFormData.order_mode === "delivery";

            const designated =
                resolvedFormData.order_mode === "takeaway"
                    ? ""
                    : resolvedFormData.order_mode === "delivery"
                      ? ""
                      : resolvedFormData.designated_number === "" ||
                          resolvedFormData.designated_number === null ||
                          resolvedFormData.designated_number === undefined
                        ? resolvedFormData.order_mode === "dine_in"
                            ? "DINE_IN"
                            : ""
                        : String(resolvedFormData.designated_number);

            const finalData = {
                notes: resolvedFormData.notes || "",
                estimated_preparation_time: normalizedPrepTime,
                ...(showExpectedStartTime
                    ? { expected_start_time: resolvedFormData.expected_start_time || "" }
                    : {}),
                alert_after_minutes: "",
                alert_time: "",
                designated_number: designated,
                use_delivery_provider: normalizedUseDeliveryProvider,
                delivery_provider: normalizedUseDeliveryProvider
                    ? resolvedFormData.delivery_provider || ""
                    : "",
                customer: resolvedFormData.customer || "",
                mobile_no: sanitizeMobileNumber(resolvedFormData.mobile_no),
                address_line1: resolvedFormData.address_line1 || "",
                address_line2: resolvedFormData.address_line2 || "",
                customer_details: resolvedFormData.customer_details || "",
                __action: actionType,
            };
            if (showDraftAlertField) {
                const alertAfterMinutes = parseInt(
                    resolvedFormData.alert_after_minutes,
                    10
                );
                if (
                    Number.isFinite(alertAfterMinutes) &&
                    alertAfterMinutes > 0
                ) {
                    finalData.alert_after_minutes = alertAfterMinutes;
                    finalData.alert_time = formatFrappeDateTime(
                        new Date(Date.now() + alertAfterMinutes * 60 * 1000)
                    );
                }
            }
            setOpen(false);
            onResolve(finalData);
            return;
        }
    };

    const handleConfirm = async () => {
        await resolveWithAction("confirm");
    };
    const handleSaveDraft = async () => {
        await resolveWithAction("save_draft");
    };

    const handleCancel = () => {
        setOpen(false);
        onResolve(null);
    };

    const handleKeyDown = (event) => {
        // Allow Ctrl+Enter or Cmd+Enter to submit
        if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            handleConfirm();
        }
    };

    return (
        <StyledEngineProvider injectFirst>
            <LocalizationProvider>
                <ThemeProvider>
                    <Dialog
                        open={open}
                        onClose={handleCancel}
                        maxWidth="sm"
                        fullWidth
                        TransitionComponent={Fade}
                        transitionDuration={{ enter: 400, exit: 300 }}
                        PaperProps={{
                            sx: (theme) => ({
                                boxShadow:
                                    theme.customShadows?.dialog ||
                                    theme.shadows[24],
                                overflow: "visible",
                            }),
                        }}
                        sx={{
                            "& .MuiBackdrop-root": {
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                backdropFilter: "blur(4px)",
                            },
                        }}
                    >
                        <DialogTitle
                            sx={(theme) => ({
                                pb: 1,
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    backgroundColor: theme.palette.primary.lighter,
                                    color: theme.palette.primary.main,
                                })}
                            >
                                <Iconify icon="eva:calendar-outline" width={14} />
                            </Box>
                            {dialogTitle}
                        </DialogTitle>
                        <DialogContent sx={{ pb: 1 }}>
                            <DialogContentText
                                sx={(theme) => ({
                                    mb: 2.5,
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.95rem",
                                    lineHeight: 1.5,
                                })}
                            >
                                {message}
                            </DialogContentText>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2.5,
                            }}
                        >
                            {showExpectedStartTime && (
                                <TextField
                                    select
                                    fullWidth
                                    label={t("pos.reservationPrompt.expectedStartTime")}
                                    value={formData.expected_start_time || ""}
                                    onChange={(event) => handleInputChange("expected_start_time", event.target.value)}
                                    SelectProps={{
                                        displayEmpty: true,
                                        renderValue: () => formData.expected_start_time || "—",
                                        onOpen: () => setCurrentTime(new Date()),
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    helperText={expectedStartTimeOptions.length === 0
                                        ? t("pos.reservationPrompt.noRemainingStartTimes") : ""}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {formData.expected_start_time && !expectedStartTimeOptions.includes(formData.expected_start_time) && (
                                        <MenuItem value={formData.expected_start_time} disabled sx={{ display: "none" }}>
                                            {formData.expected_start_time}
                                        </MenuItem>
                                    )}
                                    {expectedStartTimeOptions.map((time) => (
                                        <MenuItem key={time} value={time}>{time}</MenuItem>
                                    ))}
                                </TextField>
                            )}
                            {!isTabsPos ? (
                                <>
                                    {/* Guest Count Fields */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Adults"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.adults}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "adults",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 1,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Kids"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.kids}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "kids",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 0,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Babies"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.babies}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "babies",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 0,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={showDesignatedNumberField ? 6 : 12}>
                                            <TextField
                                                autoFocus
                                                select
                                                label="Estimated Preparation Time"
                                                fullWidth
                                                variant="outlined"
                                                value={
                                                    formData.estimated_preparation_time
                                                }
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        "estimated_preparation_time",
                                                        e.target.value
                                                    )
                                                }
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            >
                                                {estimatedPreparationTimeOptions.map(
                                                    (t) => (
                                                        <MenuItem
                                                            key={`prep_${t}`}
                                                            value={t}
                                                        >
                                                            {t}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </TextField>
                                        </Grid>
                                        {showDraftAlertField ? (
                                            <Grid item xs={12}>
                                                <TextField
                                                    select
                                                    label={t(
                                                        "pos.reservationPrompt.alertAfter",
                                                        {
                                                            defaultValue:
                                                                "Alert after",
                                                        }
                                                    )}
                                                    fullWidth
                                                    variant="outlined"
                                                    value={
                                                        formData.alert_after_minutes
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            "alert_after_minutes",
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={(theme) => ({
                                                        "& .MuiOutlinedInput-root": {
                                                            "&:hover .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    borderColor:
                                                                        theme.palette
                                                                            .primary.main,
                                                                },
                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    borderColor:
                                                                        theme.palette
                                                                            .primary.main,
                                                                    borderWidth: 2,
                                                                },
                                                        },
                                                    })}
                                                >
                                                    {alertAfterOptions.map((minutes) => (
                                                        <MenuItem
                                                            key={`alert_${minutes || "none"}`}
                                                            value={minutes}
                                                        >
                                                            {minutes
                                                                ? t(
                                                                      "pos.reservationPrompt.alertAfterMinutes",
                                                                      {
                                                                          minutes,
                                                                          defaultValue:
                                                                              "{{minutes}} minutes",
                                                                      }
                                                                  )
                                                                : t(
                                                                      "pos.reservationPrompt.noAlert",
                                                                      {
                                                                          defaultValue:
                                                                              "No alert",
                                                                      }
                                                                  )}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        ) : null}
                                        {showDesignatedNumberField ? (
                                            <Grid item xs={6}>
                                                <Autocomplete
                                                    autoHighlight
                                                    options={
                                                        designatedNumberTypeaheadOptions
                                                    }
                                                    value={
                                                        designatedNumberTypeaheadOptions.find(
                                                            (option) =>
                                                                option.value ===
                                                                formData.designated_number
                                                        ) ||
                                                        designatedNumberTypeaheadOptions[0] ||
                                                        null
                                                    }
                                                    onChange={(_, selectedOption) =>
                                                        handleInputChange(
                                                            "designated_number",
                                                            selectedOption?.value ??
                                                                "DINE_IN"
                                                        )
                                                    }
                                                    isOptionEqualToValue={(
                                                        option,
                                                        value
                                                    ) =>
                                                        option.value === value.value
                                                    }
                                                    getOptionLabel={(option) =>
                                                        option?.label || ""
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Designated Number"
                                                            fullWidth
                                                            variant="outlined"
                                                            sx={(theme) => ({
                                                                "& .MuiOutlinedInput-root": {
                                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                                        {
                                                                            borderColor:
                                                                                theme.palette
                                                                                    .primary.main,
                                                                        },
                                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                                        {
                                                                            borderColor:
                                                                                theme.palette
                                                                                    .primary.main,
                                                                            borderWidth: 2,
                                                                        },
                                                                },
                                                            })}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        ) : null}
                                        <Grid item xs={12} sx={{ pt: "4px !important" }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 2,
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={
                                                                formData.order_mode ===
                                                                "dine_in"
                                                            }
                                                            onChange={(e) => {
                                                                if (!e.target.checked) return;
                                                                handleOrderModeChange(
                                                                    "dine_in"
                                                                );
                                                            }}
                                                        />
                                                    }
                                                    label={t(
                                                        "pos.reservationPrompt.orderModeDineIn",
                                                        {
                                                            defaultValue:
                                                                "Dine in",
                                                        }
                                                    )}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={
                                                                formData.order_mode ===
                                                                "takeaway"
                                                            }
                                                            onChange={(e) => {
                                                                if (!e.target.checked) return;
                                                                handleOrderModeChange(
                                                                    "takeaway"
                                                                );
                                                            }}
                                                        />
                                                    }
                                                    label={t(
                                                        "pos.reservationPrompt.orderModeTakeaway",
                                                        {
                                                            defaultValue:
                                                                "Takeaway",
                                                        }
                                                    )}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={
                                                                formData.order_mode ===
                                                                "delivery"
                                                            }
                                                            onChange={(e) => {
                                                                if (!e.target.checked) return;
                                                                const existingCustomerText =
                                                                    String(
                                                                        formData.customer || ""
                                                                    ).trim();
                                                                const existingMobileText =
                                                                    String(
                                                                        formData.mobile_no || ""
                                                                    ).trim();
                                                                handleOrderModeChange(
                                                                    "delivery"
                                                                );
                                                                if (
                                                                    existingCustomerText
                                                                ) {
                                                                    setCustomerInputValue(
                                                                        existingCustomerText
                                                                    );
                                                                }
                                                                if (
                                                                    existingMobileText
                                                                ) {
                                                                    setMobileInputValue(
                                                                        sanitizeMobileNumber(
                                                                            existingMobileText
                                                                        )
                                                                    );
                                                                }
                                                                const providerText =
                                                                    String(
                                                                        formData.delivery_provider ||
                                                                            ""
                                                                    ).trim();
                                                                if (
                                                                    providerText
                                                                ) {
                                                                    const match =
                                                                        deliveryProviders.find(
                                                                            (
                                                                                p
                                                                            ) =>
                                                                                p?.name ===
                                                                                providerText
                                                                        );
                                                                    setDeliveryProviderInputValue(
                                                                        match?.company ||
                                                                            providerText
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label={t(
                                                        "pos.reservationPrompt.orderModeDelivery",
                                                        {
                                                            defaultValue:
                                                                "Delivery",
                                                        }
                                                    )}
                                                />
                                            </Box>
                                        </Grid>

                                        {isDeliveryMode ? (
                                            <>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        loading={deliveryProvidersLoading}
                                                        options={deliveryProviders}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.company || opt?.name || "";
                                                        }}
                                                        isOptionEqualToValue={(opt, val) =>
                                                            opt?.name === val?.name
                                                        }
                                                        value={
                                                            deliveryProviders.find(
                                                                (p) =>
                                                                    p?.name ===
                                                                    formData.delivery_provider
                                                            ) || null
                                                        }
                                                        inputValue={deliveryProviderInputValue}
                                                        onInputChange={(_, value) => {
                                                            setDeliveryProviderInputValue(value || "");
                                                        }}
                                                        onChange={(_, newValue) => {
                                                            // Strict select: only a real provider can be chosen.
                                                            if (!newValue) {
                                                                handleInputChange(
                                                                    "delivery_provider",
                                                                    ""
                                                                );
                                                                setDeliveryProviderInputValue("");
                                                                return;
                                                            }
                                                            handleInputChange(
                                                                "delivery_provider",
                                                                newValue.name
                                                            );
                                                            setDeliveryProviderInputValue(
                                                                newValue.company || newValue.name
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "pos.reservationPrompt.deliveryProvider",
                                                                    {
                                                                        defaultValue:
                                                                            "Delivery Provider",
                                                                    }
                                                                )}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        freeSolo
                                                        loading={
                                                            customerLoading ||
                                                            customerFromMobileLoading
                                                        }
                                                        options={customerOptions}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.customer_name || opt?.name || "";
                                                        }}
                                                        inputValue={customerInputValue}
                                                        onInputChange={(_, value, reason) => {
                                                            if (isSyncingRef.current) return;
                                                            if (reason !== "input") return;
                                                            const nextValue = value || "";
                                                            setCustomerInputValue(nextValue);
                                                            handleInputChange("customer", nextValue);
                                                        }}
                                                        onChange={async (_, newValue) => {
                                                            if (isSyncingRef.current) return;
                                                            if (!newValue) return;
                                                            if (typeof newValue === "string") {
                                                                handleInputChange("customer", newValue);
                                                                const resolvedByText =
                                                                    await resolveCustomerByValue(newValue);
                                                                if (resolvedByText) {
                                                                    const resolvedCustomer = String(
                                                                        resolvedByText?.name || ""
                                                                    ).trim();
                                                                    if (
                                                                        hydratedCustomerRef.current !==
                                                                        resolvedCustomer
                                                                    ) {
                                                                        resetDeliveryDirtyFields();
                                                                        await applySelectedCustomer(
                                                                            resolvedByText
                                                                        );
                                                                    }
                                                                }
                                                                return;
                                                            }
                                                            const selectedCustomer = String(
                                                                newValue?.name || ""
                                                            ).trim();
                                                            if (
                                                                hydratedCustomerRef.current !==
                                                                selectedCustomer
                                                            ) {
                                                                resetDeliveryDirtyFields();
                                                                await applySelectedCustomer(newValue);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "pos.reservationPrompt.customer",
                                                                    { defaultValue: "Customer" }
                                                                )}
                                                                fullWidth
                                                                variant="outlined"
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {customerFromMobileLoading ? (
                                                                                <CircularProgress
                                                                                    color="inherit"
                                                                                    size={20}
                                                                                />
                                                                            ) : null}
                                                                            {
                                                                                params
                                                                                    .InputProps
                                                                                    .endAdornment
                                                                            }
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Autocomplete
                                                        freeSolo
                                                        loading={mobileLoading}
                                                        options={mobileOptions}
                                                        filterOptions={(options) => options}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.mobile_no || "";
                                                        }}
                                                        inputValue={mobileInputValue}
                                                        onInputChange={(_, value, reason) => {
                                                            if (isSyncingRef.current) return;
                                                            if (reason !== "input") return;
                                                            const nextValue =
                                                                sanitizeMobileNumber(value);
                                                            markDeliveryFieldDirty("mobile_no");
                                                            setMobileInputValue(nextValue);
                                                            handleInputChange("mobile_no", nextValue);
                                                        }}
                                                        onChange={async (_, newValue) => {
                                                            if (isSyncingRef.current) return;
                                                            if (!newValue) {
                                                                markDeliveryFieldDirty("mobile_no");
                                                                setMobileInputValue("");
                                                                handleInputChange("mobile_no", "");
                                                                return;
                                                            }
                                                            if (typeof newValue === "string") {
                                                                const typedMobile =
                                                                    sanitizeMobileNumber(newValue);
                                                                markDeliveryFieldDirty("mobile_no");
                                                                setMobileInputValue(typedMobile);
                                                                handleInputChange(
                                                                    "mobile_no",
                                                                    typedMobile
                                                                );
                                                                await resolveAndApplyCustomerFromMobile(
                                                                    typedMobile
                                                                );
                                                                return;
                                                            }
                                                            const selectedMobile =
                                                                sanitizeMobileNumber(
                                                                    newValue?.mobile_no
                                                                );
                                                            if (
                                                                hydratedMobileRef.current !==
                                                                selectedMobile
                                                            ) {
                                                                resetDeliveryDirtyFields();
                                                                await applyCustomerFromMobile(newValue);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "pos.reservationPrompt.mobileNumber",
                                                                    {
                                                                        defaultValue:
                                                                            "Mobile number",
                                                                    }
                                                                )}
                                                                fullWidth
                                                                variant="outlined"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    inputMode: "numeric",
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        label={t(
                                                            "pos.reservationPrompt.addressLine1",
                                                            { defaultValue: "Address line 1" }
                                                        )}
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.address_line1}
                                                        onChange={(e) => {
                                                            markDeliveryFieldDirty(
                                                                "address_line1"
                                                            );
                                                            handleInputChange(
                                                                "address_line1",
                                                                e.target.value
                                                            );
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label={t(
                                                            "pos.reservationPrompt.addressLine2",
                                                            { defaultValue: "Address line 2" }
                                                        )}
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.address_line2}
                                                        onChange={(e) => {
                                                            markDeliveryFieldDirty(
                                                                "address_line2"
                                                            );
                                                            handleInputChange(
                                                                "address_line2",
                                                                e.target.value
                                                            );
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label={t(
                                                            "pos.reservationPrompt.customerNotes",
                                                            {
                                                                defaultValue:
                                                                    "Customer notes",
                                                            }
                                                        )}
                                                        fullWidth
                                                        variant="outlined"
                                                        multiline
                                                        rows={2}
                                                        value={formData.customer_details}
                                                        onChange={(e) => {
                                                            markDeliveryFieldDirty(
                                                                "customer_details"
                                                            );
                                                            handleInputChange(
                                                                "customer_details",
                                                                e.target.value
                                                            );
                                                        }}
                                                    />
                                                </Grid>
                                            </>
                                        ) : (
                                            <>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label="Customer"
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.customer}
                                                        onChange={(e) => {
                                                            handleInputChange(
                                                                "customer",
                                                                e.target.value
                                                            );
                                                        }}
                                                        InputProps={{
                                                            endAdornment:
                                                                customerFromMobileLoading ? (
                                                                    <CircularProgress
                                                                        color="inherit"
                                                                        size={20}
                                                                    />
                                                                ) : null,
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        freeSolo
                                                        loading={mobileLoading}
                                                        options={mobileOptions}
                                                        filterOptions={(options) => options}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.mobile_no || "";
                                                        }}
                                                        inputValue={mobileInputValue}
                                                        onInputChange={(_, value, reason) => {
                                                            if (isSyncingRef.current) return;
                                                            if (reason !== "input") return;
                                                            const nextValue =
                                                                sanitizeMobileNumber(value);
                                                            setMobileInputValue(nextValue);
                                                            handleInputChange("mobile_no", nextValue);
                                                        }}
                                                        onChange={async (_, newValue) => {
                                                            if (isSyncingRef.current) return;
                                                            if (!newValue) {
                                                                setMobileInputValue("");
                                                                handleInputChange("mobile_no", "");
                                                                return;
                                                            }
                                                            if (typeof newValue === "string") {
                                                                const typedMobile =
                                                                    sanitizeMobileNumber(newValue);
                                                                setMobileInputValue(typedMobile);
                                                                handleInputChange(
                                                                    "mobile_no",
                                                                    typedMobile
                                                                );
                                                                await resolveAndApplyCustomerFromMobile(
                                                                    typedMobile
                                                                );
                                                                return;
                                                            }
                                                            const selectedMobile =
                                                                sanitizeMobileNumber(
                                                                    newValue?.mobile_no
                                                                );
                                                            setMobileInputValue(selectedMobile);
                                                            handleInputChange(
                                                                "mobile_no",
                                                                selectedMobile
                                                            );
                                                            await applyCustomerFromMobile(newValue);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "pos.reservationPrompt.mobileNumber",
                                                                    {
                                                                        defaultValue:
                                                                            "Mobile number",
                                                                    }
                                                                )}
                                                                fullWidth
                                                                variant="outlined"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    inputMode: "numeric",
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </>
                            )}

                            {isTabsPos ? (
                                <Accordion
                                    defaultExpanded={false}
                                    disableGutters
                                    sx={(theme) => ({
                                        boxShadow: "none",
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        "&:before": { display: "none" },
                                    })}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="reservation-notes-content"
                                        id="reservation-notes-header"
                                    >
                                        {t("pos.notes", { defaultValue: "Notes" })}
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TextField
                                            label={t("pos.notes", { defaultValue: "Notes" })}
                                            fullWidth
                                            variant="outlined"
                                            value={formData.notes}
                                            onChange={(e) =>
                                                handleInputChange("notes", e.target.value)
                                            }
                                            onKeyDown={handleKeyDown}
                                            placeholder="Enter any special notes or requirements..."
                                            multiline
                                            rows={3}
                                            helperText="Press Ctrl+Enter to submit quickly"
                                            sx={(theme) => ({
                                                "& .MuiOutlinedInput-root": {
                                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                                        {
                                                            borderColor:
                                                                theme.palette.primary.main,
                                                        },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                        {
                                                            borderColor:
                                                                theme.palette.primary.main,
                                                            borderWidth: 2,
                                                        },
                                                },
                                                "& .MuiFormHelperText-root": {
                                                    color: theme.palette.text.disabled,
                                                    fontSize: "0.8rem",
                                                },
                                            })}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            ) : (
                                <TextField
                                    autoFocus
                                    label="Notes"
                                    fullWidth
                                    variant="outlined"
                                    value={formData.notes}
                                    onChange={(e) =>
                                        handleInputChange("notes", e.target.value)
                                    }
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter any special notes or requirements..."
                                    multiline
                                    rows={3}
                                    helperText="Press Ctrl+Enter to submit quickly"
                                    sx={(theme) => ({
                                        "& .MuiOutlinedInput-root": {
                                            "&:hover .MuiOutlinedInput-notchedOutline":
                                                {
                                                    borderColor:
                                                        theme.palette.primary.main,
                                                },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                {
                                                    borderColor:
                                                        theme.palette.primary.main,
                                                    borderWidth: 2,
                                                },
                                        },
                                        "& .MuiFormHelperText-root": {
                                            color: theme.palette.text.disabled,
                                            fontSize: "0.8rem",
                                        },
                                    })}
                                />
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
                        <Button
                            onClick={handleCancel}
                            variant="outlined"
                            sx={(theme) => ({
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.grey[600],
                                "&:hover": {
                                    borderColor: theme.palette.grey[400],
                                    backgroundColor: theme.palette.grey[50],
                                },
                            })}
                        >
                            {cancelText}
                        </Button>
                        {showSaveDraftButton && isTabsPos ? (
                            <Button
                                onClick={handleSaveDraft}
                                variant="outlined"
                                color="primary"
                                sx={{ minWidth: 100 }}
                            >
                                {saveDraftText}
                            </Button>
                        ) : null}
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            color="primary"
                            sx={(theme) => ({
                                minWidth: 100,
                                boxShadow:
                                    theme.customShadows?.primary ||
                                    theme.shadows[8],
                                "&:hover": {
                                    boxShadow:
                                        theme.customShadows?.primary ||
                                        theme.shadows[12],
                                },
                            })}
                        >
                            {confirmText}
                        </Button>
                    </DialogActions>
                    </Dialog>
                </ThemeProvider>
            </LocalizationProvider>
        </StyledEngineProvider>
    );
};

export const showReservationPrompt = (options = {}) => {
    return new Promise((resolve, reject) => {
        // Create a container for the dialog
        const container = document.createElement("div");
        document.body.appendChild(container);

        const root = ReactDOM.createRoot(container);

        const cleanup = () => {
            // Cleanup after dialog closes
            setTimeout(() => {
                root.unmount();
                document.body.removeChild(container);
            }, 300); // Small delay to allow dialog close animation
        };

        const handleResolve = (formData) => {
            cleanup();
            resolve(formData);
        };

        const handleReject = (error) => {
            cleanup();
            reject(error);
        };

        root.render(
            <ReservationPromptDialog
                onResolve={handleResolve}
                onReject={handleReject}
                options={options}
            />
        );
    });
};

export default ReservationPromptDialog;
